import {Component, Input} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {PebblModalComponent} from '../pebbl-modal/pebbl-modal.component';

/**
 * Reusable dark "Pebbl" confirmation dialog (console-v2 `.confirm`), built on the
 * shared PebblModalComponent shell. Title + body render via the shell's
 * title/subtitle; the footer is Cancel (secondary) + a confirm button that turns
 * into the red destructive `.btn-danger` when `danger` is set.
 *
 * Open it with NgbModal and read the boolean result:
 *   const ref = this.ngbModal.open(PebblConfirmComponent, {
 *     centered: true, windowClass: 'pebbl-modal-window pebbl-confirm-window'
 *   });
 *   ref.componentInstance.title = 'Suspend Acme?';
 *   ref.componentInstance.message = '…';
 *   ref.componentInstance.confirmLabel = 'Suspend Account';
 *   ref.componentInstance.danger = true;
 *   ref.result.then(ok => { if (ok) { … } }, () => { });
 */
/**
 * One run of a confirmation message; `bold` marks the part to emphasise and
 * `paragraph` starts a new paragraph before the run.
 */
export interface ConfirmMessagePart {
  text: string;
  bold?: boolean;
  paragraph?: boolean;
}

@Component({
  selector: 'app-pebbl-confirm',
  standalone: true,
  imports: [PebblModalComponent],
  styleUrls: ['../pebbl-modal/pebbl-dialog.css', './pebbl-confirm.component.css'],
  host: {'[class.pc-v2]': 'v2Layout', '[class.pc-nf]': 'notesLayout'},
  template: `
    <app-pebbl-modal [title]="title" [subtitle]="messageParts?.length ? '' : message">
      <!-- A message that needs to EMPHASISE part of itself - console-v2's
           confirmations bold the record they are about ("remove the link to
           <b>Q-WEB-8296</b>") - cannot come through the shell's plain-text
           \`subtitle\` input. \`messageParts\` renders the same paragraph with the
           marked segments bold, in the shell's own head slot so the spacing is
           identical either way. Callers that only need plain text keep passing
           \`message\` and nothing about them changes.
           Each \`paragraph\` part opens a sibling <p>, as console-v2 writes a second
           \`.confirm-text\` (10px apart); a <br><br> inside one <p> left a 22px gap.
           The @for is the slot's direct child on purpose: wrapped in an @if, its
           <p>s would no longer project by their \`pm-head-sub\` attribute. -->
      @for (para of paragraphs; track $index) {
        <p class="pm-subtitle" pm-head-sub>@for (part of para; track $index) {@if (part.bold) {<b>{{ part.text }}</b>}@else {<span>{{ part.text }}</span>}}</p>
      }
      <div class="pm-foot">
        <!-- An empty cancelLabel makes this a one-button notice ("OK"). -->
        @if (cancelLabel) {
          <button type="button" class="btn-secondary" (click)="onCancel()">{{ cancelLabel }}</button>
        }
        <button type="button" [class]="danger ? 'btn-danger' : 'btn-primary'"
                (click)="activeModal.close(confirmResult)">{{ confirmLabel }}</button>
      </div>
    </app-pebbl-modal>
  `
})

export class PebblConfirmComponent {
  @Input() title = '';
  @Input() message = '';
  /** Rich alternative to `message` — see the template. Takes precedence. */
  @Input() messageParts?: ConfirmMessagePart[];
  @Input() confirmLabel = 'Confirm';
  @Input() cancelLabel = 'Cancel';
  @Input() danger = false;

  /**
   * Opt in to console-v2's exact `.confirm` geometry (pebbl-confirm.component.css):
   * buttons 22px under the copy, labels that never break (the row wraps instead),
   * and the narrow gutter from 768px. Off by default, so other confirms keep their
   * current spacing; the sample-request move-back and archive/unarchive dialogs set it.
   */
  @Input() v2Layout = false;

  /**
   * Opt in to console-v2's notes-feed delete confirm (`.nf-confirm` in
   * notes-feed.component.ts) instead of its general `.confirm`: 380px wide, a
   * 22/24/20 inset, a 15px title, 13px copy and a compact Cancel. That is the
   * dialog every note, reply and attachment delete opens there. Geometry only;
   * the caller's wording is untouched. Set by the internal-notes card.
   */
  @Input() notesLayout = false;

  /**
   * What Confirm resolves with. `true` for the usual "did they agree?" caller.
   *
   * A caller replacing an older dialog can pass that dialog's own result object
   * instead, so its handler keeps working unchanged — see the sample-request
   * status confirms, which resolve with the legacy
   * `{is_send_mail, is_save, payment_mode, requestType, is_converted}` model.
   */
  @Input() confirmResult: any = true;

  /**
   * What Cancel does.
   *
   * By default Cancel DISMISSES, so `ref.result` rejects and Cancel is
   * indistinguishable from Escape or the backdrop — which is right for a plain
   * "are you sure?".
   *
   * Set it when Cancel is a real answer rather than a way out: the Convert to
   * Quote confirm's "No" starts the feedback flow, and it has to be told apart
   * from a dismissal, which must do nothing at all. When set, Cancel CLOSES
   * with this value.
   */
  @Input() declineResult: any = undefined;

  constructor(public activeModal: NgbActiveModal) {
  }

  /** `messageParts` split at each `paragraph` part. A getter, because callers push parts after open. */
  get paragraphs(): ConfirmMessagePart[][] {
    const out: ConfirmMessagePart[][] = [];
    for (const part of this.messageParts ?? []) {
      if (part.paragraph || !out.length) {
        out.push([]);
      }
      out[out.length - 1].push(part);
    }
    return out;
  }

  onCancel(): void {
    if (this.declineResult === undefined) {
      this.activeModal.dismiss(false);
      return;
    }
    this.activeModal.close(this.declineResult);
  }
}
